import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  Card,
  Divider,
  Icon,
  Logo,
  MoreButton,
  Table,
  Tag,
  Tooltip,
} from '@makeably/creativex-design-system';
import Accordion from 'components/atoms/Accordion';
import ChannelLogo from 'components/atoms/ChannelLogo';
import ScoreRow, {
  renderScore,
  statusIcon,
} from 'components/audit/ScoreRow';
import styles from './AuditPostCard.module.css';

const rulePropTypes = PropTypes.shape({
  description: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  passed: PropTypes.bool.isRequired,
  weight: PropTypes.string,
});

export const postPropTypes = {
  adAccountId: PropTypes.string,
  adId: PropTypes.string,
  brand: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(rulePropTypes).isRequired,
  channel: PropTypes.string.isRequired,
  channelDisplay: PropTypes.string.isRequired,
  contentType: PropTypes.string.isRequired,
  defaultRank: PropTypes.string,
  defaultScore: PropTypes.number,
  defaultTier: PropTypes.string,
  description: PropTypes.string.isRequired,
  isPreflight: PropTypes.bool.isRequired,
  market: PropTypes.string.isRequired,
  placements: PropTypes.arrayOf(PropTypes.string).isRequired,
  postUrl: PropTypes.string,
  regulatory: PropTypes.shape({
    rating: PropTypes.string,
    rules: PropTypes.arrayOf(rulePropTypes),
  }).isRequired,
  scores: PropTypes.arrayOf(
    PropTypes.shape({
      defaultScore: PropTypes.bool.isRequired,
      guidelines: PropTypes.arrayOf(
        PropTypes.shape({
          definitions: PropTypes.arrayOf(rulePropTypes),
          description: PropTypes.string.isRequired,
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
          passed: PropTypes.bool,
          templateUrl: PropTypes.string,
          weight: PropTypes.string,
        }),
      ).isRequired,
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      rank: PropTypes.string,
      tier: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      value: PropTypes.number,
    }),
  ).isRequired,
  snapshots: PropTypes.arrayOf(
    PropTypes.shape({
      details: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }),
      ),
      version: PropTypes.string.isRequired,
    }),
  ).isRequired,
  summaryMetrics: PropTypes.shape({
    brand: PropTypes.string.isRequired,
    campaignObjective: PropTypes.string.isRequired,
    cpc: PropTypes.string,
    ctr: PropTypes.string,
    impressions: PropTypes.string,
    market: PropTypes.string.isRequired,
    spend: PropTypes.string,
  }).isRequired,
  thirdPartyScores: PropTypes.arrayOf(PropTypes.shape({
    link: PropTypes.string,
    name: PropTypes.string.isRequired,
    scores: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      primary: PropTypes.bool.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.number,
        PropTypes.string,
      ]).isRequired,
    })).isRequired,
    status: PropTypes.string.isRequired,
  })).isRequired,
};

const propTypes = {
  assetType: PropTypes.string.isRequired,
  onTemplateClick: PropTypes.func.isRequired,
  ...postPropTypes,
};

// NOTE: Disabling because corresponding propTypes declarations are spread into propTypes component
/*  eslint-disable react/default-props-match-prop-types */
const defaultProps = {
  defaultRank: null,
  defaultScore: null,
  defaultTier: null,
  postUrl: null,
};
/*  eslint-enable react/default-props-match-prop-types */

const tooltip = "Your company's default score for this activation";

const versionsHeaders = [
  { value: 'Version' },
  { value: 'Date Used' },
  { value: 'Score' },
  { value: 'Spend' },
  { value: 'CTA' },
  { value: 'Description' },
];

const metricHeaders = [
  {
    key: 'brand',
    label: 'Brand',
    showForPreflight: true,
  },
  {
    key: 'market',
    label: 'Market',
    showForPreflight: true,
  },
  {
    key: 'campaignObjective',
    label: 'Campaign Objective',
    showForPreflight: true,
  },
  {
    key: 'spend',
    label: 'Spend (USD)',
    showForPreflight: false,
  },
  {
    key: 'impressions',
    label: 'Impressions',
    showForPreflight: false,
  },
  {
    key: 'ctr',
    label: 'CTR',
    showForPreflight: false,
  },
  {
    key: 'cpc',
    label: 'CPC (USD)',
    showForPreflight: false,
  },
  {
    key: 'adFormat',
    label: 'Ad Format',
    showForPreflight: true,
  },
];

function snapshotRow({ details, version }) {
  const cells = [
    {
      value: version,
    },
  ];

  details.forEach(({ _, value }) => {
    cells.push({
      value,
    });
  });

  return {
    cells,
    key: version,
  };
}

function tableRow({ name, passed }) {
  return {
    cells: [
      {
        value: name,
      },
      {
        value: statusIcon(passed),
      },
    ],
    key: name,
  };
}

const formatThirdPartyScore = (value) => {
  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }
  return value.toString();
};

function renderPostSummaryMetrics(isPreflightPost, metrics) {
  const summaryMetrics = metricHeaders.filter(({ key, showForPreflight }) => (
    metrics[key] !== null && (showForPreflight || !isPreflightPost)
  ));

  return summaryMetrics.map(({ key, label }) => (
    <div key={label} className={styles.metric}>
      <div className="t-caption-1">{ label }</div>
      <div className="t-body-1">{ metrics[key] }</div>
    </div>
  ));
}

const renderThirdPartyLogo = (logoUrl) => {
  if (logoUrl) {
    return <img alt="Platform Logo" className={styles.thirdPartyScoreIcon} src={logoUrl} />;
  }

  return (
    <Logo name="DEFAULT" />
  );
};

const renderThirdPartyScoreTitle = (link, name, scoreName) => {
  if (link) {
    return (
      <a
        className="u-flexRow u-gap-8 u-alignCenter"
        href={link}
        rel="noreferrer"
        target="_blank"
      >
        { scoreName ? `${name}: ${scoreName}` : name }
        <Icon name="externalLink" />
      </a>
    );
  }

  return (
    <div className="u-flexRow u-gap-8 u-alignCenter">
      { scoreName ? `${name}: ${scoreName}` : name }
      <Icon name="externalLink" />
    </div>
  );
};

const renderThirdPartyScore = (
  logoUrl,
  link,
  name,
  status,
  scoreName,
  primary,
  value,
) => (
  <React.Fragment key={`${name}-${scoreName}`}>
    <div className="u-flexRow u-gap-8 u-alignCenter u-justifyBetween">
      <div className="u-flexRow u-gap-8 u-alignCenter u-justifyCenter">
        <div className={styles.thirdPartyScoreIcon}>
          { renderThirdPartyLogo(logoUrl) }
        </div>
        { renderThirdPartyScoreTitle(link, name, scoreName) }
        { primary && (
          <Tag color="grey" label="Primary" />
        ) }
      </div>
      { (status === 'processing' || status === 'error')
        && <Tag color={status === 'processing' ? 'orange' : 'red'} label={status === 'processing' ? 'Pending' : 'Error'} /> }
      { value !== null && <Tag color="grey" label={formatThirdPartyScore(value)} /> }
    </div>
    <div className="u-marginBottom-24 u-marginTop-24"><Divider /></div>
  </React.Fragment>
);

const renderThirdPartyScores = ({
  link,
  logoUrl,
  name,
  scores: individualThirdPartyScores,
  status,
}) => (
  <React.Fragment key={name}>
    { (status === 'processing' || status === 'error') && (
      renderThirdPartyScore(logoUrl, link, name, status, null, false, null)
    ) }
    { status === 'completed' && individualThirdPartyScores.flatMap(({
      name: scoreName,
      value,
      primary,
    }) => (
      renderThirdPartyScore(logoUrl, link, name, status, scoreName, primary, value)
    )) }
  </React.Fragment>
);

function AuditPostCard({
  adAccountId,
  adId,
  assetType,
  brand,
  categories,
  channel,
  channelDisplay,
  contentType,
  defaultRank,
  defaultScore,
  defaultTier,
  description,
  isPreflight,
  market,
  onTemplateClick,
  placements,
  postUrl,
  regulatory,
  scores,
  snapshots,
  summaryMetrics: postSummaryMetrics,
  thirdPartyScores,
}) {
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const [regulatoryExpanded, setRegulatoryExpanded] = useState(false);
  const [versionsExpanded, setVersionsExpanded] = useState(false);

  const additionalMetricSectionVisible = snapshots.length > 0
    || categories.length > 0
    || regulatory.rules.length > 0;

  const isScoresVisible = scores.length > 0;
  let isScoreLabelVisible = false;
  if (isScoresVisible) {
    isScoreLabelVisible = !scores
      .find((score) => score.defaultScore)
      ?.guidelines
      ?.some(({ passed, weight }) => weight !== '0%' && passed === null);
  }

  const postOptions = [];
  if (postUrl) {
    postOptions.push({
      label: 'View External Link',
      newTab: true,
      url: postUrl,
    });
  }

  const scoreLabel = renderScore(defaultScore, defaultRank, defaultTier);
  const overviewClass = classNames({
    [styles.preflightScoreOverviewGrid]: isPreflight,
    [styles.inflightScoreOverviewGrid]: !isPreflight,
  });

  return (
    <Card
      className={classNames('u-flexColumn', 'u-gap-24', styles.auditPostCard)}
      padding={false}
    >
      <div className="u-flexRow u-spaceBetween">
        <div className="u-flexRow u-alignCenter u-gap-8">
          <ChannelLogo
            channel={channel}
            showPaid={false}
            size="size-32"
          />
          <h5>{ channelDisplay }</h5>
          { scoreLabel && isScoreLabelVisible && (
            <Tooltip label={tooltip}>
              { scoreLabel }
            </Tooltip>
          ) }
          <Tag color="grey" label={isPreflight ? 'Pre-Flight' : 'In-Flight'} />
        </div>
        <div className="u-flexRow u-gap-8">
          <div className="t-caption-1">
            { adId && (
              <div className="u-textAlignRight">
                { `Ad ID: ${adId}` }
              </div>
            ) }
            { adAccountId && (
              <div className="u-textAlignRight">
                { `Ad Account Id: ${adAccountId}` }
              </div>
            ) }
          </div>
          { postOptions.length > 0 && <MoreButton options={postOptions} /> }
        </div>
      </div>
      <h5>Overview</h5>
      <div className={overviewClass}>
        { renderPostSummaryMetrics(isPreflight, postSummaryMetrics) }
      </div>
      <div className={styles.metric}>
        <div className="t-caption-1">Placements</div>
        <div className="t-body-1">{ placements.length > 0 ? placements.join(' | ') : 'N/A' }</div>
      </div>
      <div className={classNames(styles.metric, { 'u-marginBottom-24': additionalMetricSectionVisible && !isScoresVisible })}>
        <div className="t-caption-1">Description</div>
        <div className="t-body-1">{ description }</div>
      </div>
      { isScoresVisible && (
        <div>
          <h5 className="u-marginBottom-24">Scores</h5>
          { scores.map((score) => (
            <ScoreRow
              key={score.id}
              assetType={assetType}
              brand={brand}
              channel={channel}
              contentType={contentType}
              guidelines={score.guidelines}
              isDefaultScore={score.defaultScore}
              isPreflight={isPreflight}
              label={score.label}
              market={market}
              placements={placements}
              rank={score.rank}
              scoreId={score.id}
              tier={score.tier}
              value={score.value}
              onTemplateClick={onTemplateClick}
            />
          )) }
        </div>
      ) }
      { thirdPartyScores && thirdPartyScores.length > 0 && (
        <div className="u-marginBottom-24">
          <h5 className="u-marginBottom-24">
            Third Party Scores
          </h5>
          { thirdPartyScores.flatMap(renderThirdPartyScores) }
        </div>
      ) }
      { additionalMetricSectionVisible && (
        <div className="u-flexColumn u-gap-24">
          { snapshots.length > 0 && (
            <Accordion
              chevronOnRight={false}
              divider={false}
              header={(
                <div className={styles.headerRow}>
                  { `${versionsExpanded ? 'Hide' : 'Show'} Versions` }
                </div>
              )}
              initiallyExpanded={false}
              onClick={() => setVersionsExpanded(!versionsExpanded)}
            >
              <Table
                className={styles.table}
                headers={versionsHeaders}
                rows={snapshots.map(snapshotRow)}
              />
            </Accordion>
          ) }
          { categories.length > 0 && (
            <Accordion
              chevronOnRight={false}
              divider={false}
              header={(
                <div className={styles.headerRow}>
                  Brand Cues
                </div>
              )}
              initiallyExpanded={false}
              onClick={() => setCategoriesExpanded(!categoriesExpanded)}
            >
              <Table
                className={styles.table}
                divider={false}
                headers={[
                  { value: 'Brand Cues' },
                  {
                    className: styles.criteriaMet,
                    value: 'Criteria Met',
                  },
                ]}
                rows={categories.map(tableRow)}
              />
            </Accordion>
          ) }
          { regulatory.rules.length > 0 && (
            <Accordion
              chevronOnRight={false}
              divider={false}
              header={(
                <div className={styles.headerRow}>
                  Compliance
                </div>
              )}
              initiallyExpanded={false}
              onClick={() => setRegulatoryExpanded(!regulatoryExpanded)}
            >
              <Table
                className={styles.table}
                headers={[
                  { value: 'Name' },
                  {
                    className: styles.criteriaMet,
                    value: 'Criteria Met',
                  },
                ]}
                rows={regulatory.rules.map((tableRow))}
              />
            </Accordion>
          ) }
        </div>
      ) }
    </Card>
  );
}

AuditPostCard.propTypes = propTypes;
AuditPostCard.defaultProps = defaultProps;

export default AuditPostCard;
